import { useQueries, type QueryObserverResult } from '@tanstack/react-query'
import { useCallback } from 'react'
import { amountChange, localToday, type AmountChange } from './recurringRules'
import { periodsQueryOptions, type PeriodsResult } from './useRecurring'

export type { AmountChange } from './recurringRules'

/**
 * The cap on ids fetched: each id is one payments request. Ids past the 30th
 * are NOT fetched and are ABSENT from the result. Callers (the bills list, M8
 * "Subscriptions creep", M10) must window their ids to what is visible.
 */
export const MAX_AMOUNT_CHANGE_IDS = 30

const SEP = '\u0000'

export type AmountChanges = Record<string, AmountChange | null | undefined>

/**
 * Client-derived amount change per bill id. A value of `undefined` means
 * loading, OR the fetch failed, OR the id is past the cap (then the key is
 * absent); `null` means loaded with no change worth showing. Shares the periods
 * query cache with `usePeriods`. The result is referentially stable while the
 * ids and the fetched data are unchanged.
 */
export function useAmountChanges(ids: string[]): AmountChanges {
  const capped = ids.slice(0, MAX_AMOUNT_CHANGE_IDS)
  const today = localToday()
  const key = capped.join(SEP)
  const combine = useCallback(
    (results: QueryObserverResult<PeriodsResult>[]): AmountChanges =>
      Object.fromEntries(
        (key === '' ? [] : key.split(SEP)).map((id, i) => {
          const data = results[i]?.data
          return [id, data === undefined ? undefined : amountChange(data.periods)]
        }),
      ),
    [key],
  )
  return useQueries({ queries: capped.map((id) => periodsQueryOptions(id, today)), combine })
}
